import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  SARIMA_PREDIT,
  PREDICT_ALL,
  SARIMA_RETRAIN,
  SARIMA_RETRAIN_ALL,
  SARIMA_INGEST_IMPORTS,
  SARIMA_HEALTH,
  SARIMA_MODELS,
  SARIMA_MODELS_RUNS,
  SARIMA_MODEL_RUN_BY_ID,
  SARIMA_MODEL_LATEST_RUN,
  SARIMA_IMPORTS,
  SARIMA_IMPORT_SUMMARY,
  SARIMA_LATEST_CHART_DATA,
  SARIMA_FORECAST_BY_ID,
  SARIMA_CHART_DATA_BY_ID,
  SARIMA_TEST_PREDICTIONS,
  UPLOAD_SARIMA_IMPORTS,
} from '../graphql/smart-forecasting.graphql';
import {
  sarimaPredictActions,
  predictAllActions,
  sarimaRetrainActions,
  sarimaRetrainAllActions,
  sarimaIngestImportsActions,
  sarimaHealthActions,
  sarimaModelsActions,
  sarimaModelRunsActions,
  sarimaModelRunByIdActions,
  sarimaLatestModelRunActions,
  sarimaImportsActions,
  sarimaImportSummaryActions,
  sarimaLatestChartDataActions,
  sarimaForecastByIdActions,
  sarimaChartDataByIdActions,
  sarimaTestPredictionsActions,
  sarimaUploadImportsActions,
} from './smart-forecasting.actions';
import {
  SarimaPredictResponse,
  PredictAllResponse,
  SarimaRetrainResponse,
  SarimaRetrainAllResponse,
  SarimaIngestImportsResponse,
  SarimaHealthResponse,
  SarimaModelsResponse,
  SarimaModelRunsResponse,
  SarimaModelRunByIdResponse,
  SarimaLatestModelRunResponse,
  SarimaImportsResponse,
  SarimaImportSummaryResponse,
  SarimaLatestChartDataResponse,
  SarimaForecastByIdResponse,
  SarimaChartDataByIdResponse,
  SarimaTestPredictionsResponse,
  SarimaUploadImportsResponse,
} from '../responses/smart-forecasting.response';

@Injectable({ providedIn: 'root' })
export class SmartForecastingEffects {
  constructor(
    private actions$: Actions,
    private graphQlService: GraphQlService,
  ) {}

  sarimaPredict$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaPredictActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaPredictResponse>({
            query: SARIMA_PREDIT,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaPredict;
              if (!payload || !payload.status) {
                return sarimaPredictActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaPredictActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaPredictActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  predictAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(predictAllActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<PredictAllResponse>({
            query: PREDICT_ALL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaPredictAll;
              if (!payload || !payload.status) {
                return predictAllActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return predictAllActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                predictAllActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaRetrain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaRetrainActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SarimaRetrainResponse>({
            mutation: SARIMA_RETRAIN,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaRetrain;
              if (!payload || !payload.status) {
                return sarimaRetrainActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaRetrainActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaRetrainActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaRetrainAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaRetrainAllActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SarimaRetrainAllResponse>({
            mutation: SARIMA_RETRAIN_ALL,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaRetrainAll;
              if (!payload || !payload.status) {
                return sarimaRetrainAllActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaRetrainAllActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaRetrainAllActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaIngestImports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaIngestImportsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SarimaIngestImportsResponse>({
            mutation: SARIMA_INGEST_IMPORTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaIngestImports;
              if (!payload || !payload.status) {
                return sarimaIngestImportsActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaIngestImportsActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaIngestImportsActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaHealth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaHealthActions.request),
      switchMap(() =>
        this.graphQlService
          .query<SarimaHealthResponse>({
            query: SARIMA_HEALTH,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaHealth;
              if (!payload || !payload.status) {
                return sarimaHealthActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaHealthActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaHealthActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaModelsActions.request),
      switchMap(() =>
        this.graphQlService
          .query<SarimaModelsResponse>({
            query: SARIMA_MODELS,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaModels;
              if (!payload || !payload.status) {
                return sarimaModelsActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaModelsActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaModelsActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaModelRuns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaModelRunsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaModelRunsResponse>({
            query: SARIMA_MODELS_RUNS,
            apolloNamespace: ApolloNamespace.registration,
            variables: request ? {
              product: request.product,
              port: request.port,
              use: request.use,
              limit: request.limit,
            } : {},
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaModelRuns;
              if (!payload || !payload.status) {
                return sarimaModelRunsActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaModelRunsActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaModelRunsActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaModelRunById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaModelRunByIdActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaModelRunByIdResponse>({
            query: SARIMA_MODEL_RUN_BY_ID,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              runId: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaModelRunById;
              if (!payload || !payload.status) {
                return sarimaModelRunByIdActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaModelRunByIdActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaModelRunByIdActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaLatestModelRun$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaLatestModelRunActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaLatestModelRunResponse>({
            query: SARIMA_MODEL_LATEST_RUN,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              product: request.product,
              port: request.port,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaLatestModelRun;
              if (!payload || !payload.status) {
                return sarimaLatestModelRunActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaLatestModelRunActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaLatestModelRunActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaImports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaImportsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaImportsResponse>({
            query: SARIMA_IMPORTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              product: request.product,
              endDate: request.endDate,
              port: request.port,
              use: request.use,
              limit: request.limit,
              startDate: request.startDate,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaImports;
              if (!payload || !payload.status) {
                return sarimaImportsActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaImportsActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaImportsActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaImportSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaImportSummaryActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaImportSummaryResponse>({
            query: SARIMA_IMPORT_SUMMARY,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              product: request.product,
              endDate: request.endDate,
              port: request.port,
              use: request.use,
              startDate: request.startDate,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaImportSummary;
              if (!payload || !payload.status) {
                return sarimaImportSummaryActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaImportSummaryActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaImportSummaryActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaLatestChartData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaLatestChartDataActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaLatestChartDataResponse>({
            query: SARIMA_LATEST_CHART_DATA,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              product: request.product,
              port: request.port,
              use: request.use,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaLatestChartData;
              if (!payload || !payload.status) {
                return sarimaLatestChartDataActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaLatestChartDataActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaLatestChartDataActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaForecastById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaForecastByIdActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaForecastByIdResponse>({
            query: SARIMA_FORECAST_BY_ID,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              runId: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaForecastById;
              if (!payload || !payload.status) {
                return sarimaForecastByIdActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaForecastByIdActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaForecastByIdActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaChartDataById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaChartDataByIdActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaChartDataByIdResponse>({
            query: SARIMA_CHART_DATA_BY_ID,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              runId: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaChartDataById;
              if (!payload || !payload.status) {
                return sarimaChartDataByIdActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaChartDataByIdActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaChartDataByIdActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaTestPredictions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaTestPredictionsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<SarimaTestPredictionsResponse>({
            query: SARIMA_TEST_PREDICTIONS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              runId: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaTestPredictions;
              if (!payload || !payload.status) {
                return sarimaTestPredictionsActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaTestPredictionsActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaTestPredictionsActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  sarimaUploadImports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sarimaUploadImportsActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<SarimaUploadImportsResponse>({
            mutation: UPLOAD_SARIMA_IMPORTS,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              input: request,
            },
          })
          .pipe(
            map((response) => {
              const payload = response.data?.sarimaUploadImports;
              if (!payload || !payload.status) {
                return sarimaUploadImportsActions.failure({
                  error: payload ?? { success: false, statusCode: 400, message: 'Failed', data: null },
                });
              }
              return sarimaUploadImportsActions.success({ response: payload });
            }),
            catchError((error) =>
              of(
                sarimaUploadImportsActions.failure({
                  error: { success: false, statusCode: 400, message: error?.message ?? 'Failed', data: null },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
